import {
  ExperienceAnchor,
  IAutomationTemplate,
  isEventDrivenTiming,
  ITaskTemplate,
  ITimelineItem,
  UserField
} from '@dadohr/shared/domainTypes/experience';
import { Button, MenuItem, Select } from '@material-ui/core';
import { Clock } from 'lucide-react';
import moment from 'moment-timezone';
import { useMemo } from 'react';
import { CustomInputLabel } from '../../../../../../components/CustomInputLabel';
import { FormRow } from '../../../../../../components/Form/FormRow';
import { SwitchWithLabel } from '../../../../../../components/SwitchWithLabel';
import { TimezoneSelector } from '../../../../../../components/TimezoneSelector';
import {
  UnsafeTFunction,
  useUnsafeTranslation
} from '../../../../../../i18next';
import { FlexContainerVertical } from '../../../../../../layout/Flex';
import { useFeatureEnabled } from '../../../../../../services/features';
import { ONE_DAY, printTz } from '../../../../../../services/time';
import {
  getTimelineStartTimeFromTaskBuilderContext,
  TaskBuilderContext
} from '../types';
import { RuleRenderer, RuleBuilderConfig } from '../../../RuleBuilder';
import { SettingsSection, SideContainer } from './layout';
import { SideContainerHeader } from './SideContainerHeader';
import { TaskSettingsOverviewSection } from './TaskSettingsOverviewSection';
import { FixedTimingSummary } from './FixedTimingSummary';
import { TimelineItemTiming } from './TimelineItemTiming';
import * as UX from '../../newux/kit';
import { DaySelector, TimePicker } from './TimeSelectors';
import { TriggerList } from './TriggerList';

const ICON = Clock;

const TRIGGERS = (
  t: UnsafeTFunction
): (
  | {
      label: string;
      type: 'ABSOLUTE';
      data: {
        type: 'ABSOLUTE';
        start: number;
        startTime: { hour: number; minute: number } | null;
      };
      disabled?: boolean;
    }
  | {
      label: string;
      type: 'ON_TASK_START';
      data: {
        type: 'ON_TASK_START';
        taskId: '';
      };
      disabled?: boolean;
    }
  | {
      label: string;
      type: 'ON_TASK_COMPLETE';
      data: {
        type: 'ON_TASK_COMPLETE';
        taskId: '';
      };
      disabled?: boolean;
    }
  | {
      label: string;
      type: 'ON_TASK_LATE';
      data: {
        type: 'ON_TASK_LATE';
        taskId: '';
      };
      disabled?: boolean;
    }
)[] => [
  {
    label: t('At a specified date and time'),
    type: 'ABSOLUTE',
    data: {
      type: 'ABSOLUTE',
      start: 0,
      startTime: null
    }
  },
  {
    label: t('When task starts'),
    type: 'ON_TASK_START',
    data: {
      type: 'ON_TASK_START',
      taskId: ''
    },
    disabled: true
  },
  {
    label: t('When task is completed'),
    type: 'ON_TASK_COMPLETE',
    data: {
      type: 'ON_TASK_COMPLETE',
      taskId: ''
    },
    disabled: true
  },
  {
    label: t('When task is late'),
    type: 'ON_TASK_LATE',
    data: {
      type: 'ON_TASK_LATE',
      taskId: ''
    },
    disabled: true
  }
];

const AutomationTimingInner = ({
  value,
  allTasks,
  onGoToOtherItem,
  ruleConfig,
  anchor,
  tz
}: {
  value: IAutomationTemplate;
  allTasks: ITaskTemplate[];
  onGoToOtherItem?: (nextItem: ITimelineItem) => void;
  ruleConfig?: RuleBuilderConfig<UserField>;
  // NEW_UX: when given, the fixed-timing line is phrased in the author's chosen
  // mode (before/after milestone, calendar date). Classic callers leave it out.
  anchor?: ExperienceAnchor;
  tz?: string;
}) => {
  const { t } = useUnsafeTranslation();
  const { timing } = value;
  if (timing.type === 'fixed') {
    return (
      <>
        <div>
          {anchor && tz ? (
            <FixedTimingSummary timing={timing} anchor={anchor} tz={tz} />
          ) : (
            <UX.TimingSummaryText
              text={t('**Day {{ day }}** of experience', {
                day: Math.round(timing.start / ONE_DAY) + 1
              })}
            />
          )}
          {timing.time && (
            <span>
              {' ' + t('at') + ' '}
              {moment()
                .hour(timing.time.hour)
                .minute(timing.time.minute)
                .format('hh:mm a')}
              {timing.time.customTz ? ` ${printTz(timing.time.customTz)}` : ''}
            </span>
          )}
        </div>
      </>
    );
  }

  if (timing.type === 'dataChange') {
    const day = Math.round((timing.earliestStart ?? 0) / ONE_DAY) + 1;
    const fieldLabel = timing.watchField
      ? ruleConfig?.[timing.watchField]?.label
      : undefined;
    if (timing.watchField) {
      return (
        <UX.TimingSummaryText
          text={t('When **{{ field }}** changes, on or after **day {{ day }}** of experience', {
            field: fieldLabel || t('a field'),
            day
          })}
        />
      );
    }
    return (
      <div>
        <UX.TimingSummaryText
          text={t('When conditions met, on or after **day {{ day }}** of experience:', {
            day
          })}
        />
        {ruleConfig && (
          <div style={{ marginTop: 4 }}>
            <RuleRenderer
              value={timing.rules ?? null}
              config={ruleConfig}
              variant="oneline"
            />
          </div>
        )}
      </div>
    );
  }

  if (isEventDrivenTiming(timing)) {
    return (
      <TriggerList
        timing={timing}
        allTasks={allTasks}
        onGoToOtherItem={onGoToOtherItem}
      />
    );
  }

  console.warn(`Cannot render timing type - ${timing}`);
  return null;
};

/**
 * Read-only "Trigger" section for the NEW_UX automation viewer — headingless
 * (the shell owns no panel header now), mirroring the task/message viewers.
 * Reuses AutomationTimingInner's fixed/dataChange/event-driven text + catch-up.
 */
export const AutomationViewerTrigger = ({
  value,
  allTasks,
  onGoToOtherItem,
  ruleConfig,
  anchor,
  tz
}: {
  value: IAutomationTemplate;
  allTasks: ITaskTemplate[];
  onGoToOtherItem?: (nextItem: ITimelineItem) => void;
  ruleConfig?: RuleBuilderConfig<UserField>;
  anchor: ExperienceAnchor;
  tz: string;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <div style={{ marginBottom: 16 }}>
      <UX.FieldLabel>{t('Start')}</UX.FieldLabel>
      <div
        style={{
          color: UX.tokens.color.grey700,
          fontSize: 16,
          fontWeight: UX.tokens.font.weightScale.regular
        }}
      >
        <AutomationTimingInner
          value={value}
          allTasks={allTasks}
          onGoToOtherItem={onGoToOtherItem}
          ruleConfig={ruleConfig}
          anchor={anchor}
          tz={tz}
        />
      </div>
      {value.timing.type === 'fixed' && value.catchup?.disabled && (
        <div
          style={{
            marginTop: 4,
            fontStyle: 'italic',
            color: UX.tokens.color.grey500
          }}
        >
          {t(`Won't run in catch-up mode`)}
        </div>
      )}
    </div>
  );
};

export const AutomationSettingsOverviewSectionTrigger = ({
  title,
  divider,
  value,
  onEdit,
  allTasks,
  onGoToOtherItem
}: {
  title: string;
  divider?: boolean;
  onEdit?: () => void;
  value: IAutomationTemplate;
  allTasks: ITaskTemplate[];
  onGoToOtherItem?: (nextItem: ITimelineItem) => void;
}) => {
  return (
    <TaskSettingsOverviewSection
      icon={ICON}
      title={title}
      onEdit={onEdit}
      divider={divider}
    >
      <AutomationTimingInner
        value={value}
        allTasks={allTasks}
        onGoToOtherItem={onGoToOtherItem}
      />
    </TaskSettingsOverviewSection>
  );
};

export const AutomationSettingsTriggerV2 = ({
  title,
  description,
  onBack,
  value,
  onChange,
  expTpl,
  onGoToOtherItem,
  newUx = false
}: {
  title: string;
  description: string;
  onBack: () => void;

  value: IAutomationTemplate;
  onChange: React.Dispatch<React.SetStateAction<IAutomationTemplate>>;
  expTpl: TaskBuilderContext;
  onGoToOtherItem?: (item: ITimelineItem) => void;
  newUx?: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  if (newUx) {
    return (
      <div style={{ padding: 20, overflowY: 'auto', height: '100%' }}>
        <UX.PanelHeader
          icon={<Clock size={17} />}
          title={t('Timing & Triggers')}
          hint={t('When this automation runs.')}
        />
        <UX.SectionLabel style={{ marginTop: 0 }}>
          {t('Start')}
        </UX.SectionLabel>
        <TimelineItemTiming
          value={value.timing}
          onChange={(nextTiming) =>
            onChange((v) => ({ ...v, timing: nextTiming }))
          }
          tplContext={expTpl}
          onGoToOtherItem={onGoToOtherItem}
          newUx
        />
        {value.timing.type === 'fixed' && (
          <>
            <UX.SectionLabel>{t('Catch-up mode')}</UX.SectionLabel>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <UX.Toggle
                on={value.catchup.disabled}
                ariaLabel={t(`Don't run this item in catch-up mode`)}
                onChange={(nextChecked) =>
                  onChange((v) => ({
                    ...v,
                    catchup: { ...v.catchup, disabled: nextChecked }
                  }))
                }
              />
              <span style={{ fontSize: 13 }}>
                {t(`Don't run this item in catch-up mode`)}
              </span>
            </div>
          </>
        )}
      </div>
    );
  }
  return (
    <SideContainer>
      <SideContainerHeader
        def={{
          label: title,
          description,
          icon: ICON
        }}
        onBack={onBack}
      />
      <SettingsSection>
        <FlexContainerVertical fullWidth spacing={3}>
          <TimelineItemTiming
            value={value.timing}
            onChange={(nextTiming) =>
              onChange((v) => ({ ...v, timing: nextTiming }))
            }
            tplContext={expTpl}
            onGoToOtherItem={onGoToOtherItem}
          />

          {value.timing.type === 'fixed' && (
            <CustomInputLabel label={t('Catch-up mode ')} fullWidth>
              <SwitchWithLabel
                label={t(`Don't run this item in catch-up mode`)}
                checked={value.catchup.disabled}
                onChange={(nextChecked) =>
                  onChange((v) => ({
                    ...v,
                    catchup: {
                      ...v.catchup,
                      disabled: nextChecked
                    }
                  }))
                }
              />
            </CustomInputLabel>
          )}
        </FlexContainerVertical>
      </SettingsSection>
    </SideContainer>
  );
};

export const AutomationSettingsTrigger = ({
  title,
  description,
  onBack,
  value,
  onChange,
  expTpl
}: {
  title: string;
  description: string;
  onBack: () => void;

  value: IAutomationTemplate;
  onChange: React.Dispatch<React.SetStateAction<IAutomationTemplate>>;
  expTpl: TaskBuilderContext;
}) => {
  const { t } = useUnsafeTranslation();
  const triggers = useMemo(() => TRIGGERS(t), [t]);

  const userTimezoneHandlingEnabled = useFeatureEnabled(
    'USER_BASED_TIMEZONES_V1'
  );

  const { onChangeTrigger, onChangeAbsoluteTrigger } = useMemo(
    () => ({
      onChangeTrigger: (nextTriggerType: string) => {
        const nextTrigger = triggers.find((x) => x.type === nextTriggerType);
        if (nextTrigger) {
          onChange((v) => ({ ...v, at: nextTrigger.data }));
        }
      },
      onChangeAbsoluteTrigger: {
        onChangeStart: (nextStart: number) =>
          onChange((v) => {
            const timing = v.timing;
            if (timing.type !== 'fixed') {
              // can never happen, but satisfies the type system
              return v;
            }
            return {
              ...v,
              timing: {
                ...timing,
                start: nextStart
              }
            };
          }),
        onChangeStartTime: (time: {
          hour: number;
          minute: number;
          customTz?: string | null;
        }) =>
          onChange((v) => {
            const timing = v.timing;
            if (timing.type !== 'fixed') {
              // can never happen, but satisfies the type system
              return v;
            }
            return { ...v, timing: { ...timing, time } };
          }),
        onSetDefaultStartTime: () =>
          onChange((v) => {
            const timing = v.timing;
            if (timing.type !== 'fixed') {
              // can never happen, but satisfies the type system
              return v;
            }
            return {
              ...v,
              timing: { ...timing, time: { hour: 9, minute: 0 } }
            };
          }),
        onClearStartTime: () =>
          onChange((v) => {
            const timing = v.timing;
            if (timing.type !== 'fixed') {
              // can never happen, but satisfies the type system
              return v;
            }
            return { ...v, timing: { ...timing, time: null } };
          })
      }
    }),
    [onChange, triggers]
  );

  return (
    <SideContainer>
      <SideContainerHeader
        def={{
          label: title,
          description,
          icon: ICON
        }}
        onBack={onBack}
      />
      <SettingsSection>
        {!userTimezoneHandlingEnabled && (
          <FormRow>
            <CustomInputLabel label={t('Trigger')} fullWidth>
              <Select
                value={value.at.type}
                onChange={(ev) => onChangeTrigger(ev.target.value as string)}
                fullWidth
              >
                {triggers.map((tr) => (
                  <MenuItem
                    key={tr.type}
                    value={tr.type}
                    disabled={tr.disabled}
                  >
                    {tr.label}
                  </MenuItem>
                ))}
              </Select>
            </CustomInputLabel>
          </FormRow>
        )}

        {value.timing.type === 'fixed' && (
          <>
            <FormRow>
              <CustomInputLabel
                label={t('Time')}
                fullWidth
                info={
                  userTimezoneHandlingEnabled
                    ? undefined
                    : t(
                        'Triggers without a specified time will be sent at the same time as when the experience started'
                      )
                }
              >
                <DaySelector
                  value={value.timing.start}
                  onChange={onChangeAbsoluteTrigger.onChangeStart}
                />
                {!userTimezoneHandlingEnabled && (
                  <>
                    {!value.timing.time && (
                      <Button
                        color="primary"
                        onClick={onChangeAbsoluteTrigger.onSetDefaultStartTime}
                        size="small"
                      >
                        {t('Add Time')}
                      </Button>
                    )}

                    {value.timing.time && (
                      <TimePicker
                        t={value.timing.time}
                        minuteStep={10}
                        onChange={onChangeAbsoluteTrigger.onChangeStartTime}
                        onClear={onChangeAbsoluteTrigger.onClearStartTime}
                      />
                    )}
                  </>
                )}
              </CustomInputLabel>
            </FormRow>
            {userTimezoneHandlingEnabled && (
              <FormRow>
                <TimePicker
                  t={
                    value.timing.time ??
                    getTimelineStartTimeFromTaskBuilderContext(expTpl) ?? {
                      hour: 9,
                      minute: 0
                    }
                  }
                  minuteStep={10}
                  onChange={(st) => {
                    if (value.timing.type !== 'fixed') {
                      // can never happen
                      return;
                    }
                    return onChangeAbsoluteTrigger.onChangeStartTime({
                      ...st,
                      customTz: value.timing.time?.customTz || expTpl.tz
                    });
                  }}
                />
                <TimezoneSelector
                  fullWidth
                  value={value.timing.time?.customTz || expTpl.tz}
                  onChange={(customTz) => {
                    if (value.timing.type !== 'fixed') {
                      // can never happen
                      return;
                    }
                    return onChangeAbsoluteTrigger.onChangeStartTime({
                      ...(value.timing.time ??
                        getTimelineStartTimeFromTaskBuilderContext(expTpl) ?? {
                          hour: 9,
                          minute: 0
                        }),
                      customTz
                    });
                  }}
                />
              </FormRow>
            )}
          </>
        )}
      </SettingsSection>
    </SideContainer>
  );
};
